import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Clock3, Gamepad2 } from "lucide-react";
import UAParser from "ua-parser-js";

import styles from "../css/RakhiChallenge.module.css";

import rakhiBanner from "../../../icons/festival/janmashtami.webp";
import Header from "../../Questions/component/Header";
import coin from "../../../icons/coin.svg";
import { SoftHeader } from "../../Questions/component/RandomQuestions";

import MathGame from "../../Questions/component/MathGame";
import NumberRain from "../../Questions/component/NumberRain";
import ColorMatchGame from "../../Questions/component/ColorMatchGame";

import {
  offerEvent,
  fetchIpAddress,
  userLeaderboard,
  getQuizCategoriesQuestions,
} from "../../../utils/api";

import { ErrorHandling } from "../../../State";

const dummyChallengeData = {
  title: "Janmashtami",
  subtitle: "5-DAY CHALLENGE",
  description: "5 Days of Fun. 5 Days of Rewards.",
  description2: "Stronger the bond, bigger the rewards!",
  startDate: "21 Aug",
  endDate: "25 Aug",

  days: [
    {
      id: 1,
      day: 1,
      title: "Janmashtami Quiz-1",
      type: "quiz",
      category: "contest1",
      duration: "60-Second Quiz",
      status: "available",
      reward: null,
      theme: "red",
      gameKey: null,
      is_played: false,
    },
    {
      id: 2,
      day: 2,
      title: "Fun Math Game",
      type: "game",
      duration: "60-Second Game",
      status: "available",
      reward: 10,
      theme: "red",
      gameKey: "math",
    },
    {
      id: 3,
      day: 3,
      title: "Fun Number Rain",
      type: "game",
      duration: "60-Second Game",
      status: "available",
      reward: 10,
      theme: "red",
      gameKey: "numberRain",
    },
    {
      id: 4,
      day: 4,
      title: "Fun Color Game",
      type: "game",
      duration: "60-Second Game",
      status: "available",
      reward: 10,
      theme: "red",
      gameKey: "color",
    },
    {
      id: 5,
      day: 5,
      title: "Janmashtami Quiz-2",
      type: "quiz",
      category: "contest2",
      duration: "60-Second Quiz",
      status: "locked",
      reward: null,
      theme: "red",
      gameKey: null,
    },
  ],
};

const PLAYED_CONTESTS_KEY = "rakhiPlayedContests";

const getPlayedContests = () => {
  try {
    const data = localStorage.getItem(PLAYED_CONTESTS_KEY);

    return data ? JSON.parse(data) : {};
  } catch (error) {
    return {};
  }
};

const savePlayedContest = (contestId) => {
  const current = getPlayedContests();

  const updated = {
    ...current,
    [String(contestId)]: true,
  };

  localStorage.setItem(PLAYED_CONTESTS_KEY, JSON.stringify(updated));

  return updated;
};

function RakhiChallenge() {
  const navigate = useNavigate();

  const [ipAddress, setIpAddress] = useState("");

  // Prevent duplicate page impressions in React StrictMode
  const pageTrackedRef = useRef(false);
  const cardsTrackedRef = useRef(false);

  const [selectedGame, setSelectedGame] = useState(null);

  const [userContestScore, setUserContestScore] = useState(0);
  const [scoreLoading, setScoreLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);

  const challenge = dummyChallengeData;
  const fanduPoints = localStorage.getItem("userFanduPoints") || 0;

  const machineId = localStorage.getItem("mId") || "2026";

  const uuid = localStorage.getItem("uuid") || "";

  const sessionId = localStorage.getItem("session_id") || "";

  const userId = localStorage.getItem("userId") || "";

  const fanduUserId = localStorage.getItem("fanduUserId");

  useEffect(() => {
    const getIpAddress = async () => {
      try {
        const storedIp = localStorage.getItem("ip_address");

        if (storedIp) {
          setIpAddress(storedIp);
          return;
        }

        const response = await fetchIpAddress({
          device_id: uuid,
          session_id: sessionId,
          user_id: userId,
          machine_id: machineId,
        });

        if (response?.ip_address) {
          setIpAddress(response.ip_address);

          localStorage.setItem("ip_address", response.ip_address);
        }
      } catch (error) {
        console.log("Unable to fetch IP address:", error);
      }
    };

    getIpAddress();
  }, [machineId, sessionId, userId, uuid]);

  const offerRequestData = (eventData) => {
    try {
      const parser = new UAParser();
      const result = parser.getResult();

      return {
        machine_id: machineId || "",
        device_id: uuid || "",
        session_id: sessionId || "",
        user_id: userId || "",

        ip_address: ipAddress || localStorage.getItem("ip_address") || "",

        lat: "",
        lng: "",

        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

        firstname: "",
        lastname: "",
        email: "",
        phone: "",

        deviceBrand: result?.device?.vendor || "Unknown",

        deviceModel: result?.device?.model || "Unknown",

        deviceType: result?.device?.type || "desktop",

        osName: result?.os?.name || "Unknown",

        osVersion: result?.os?.version || "Unknown",

        browserName: result?.browser?.name || "Unknown",

        browserVersion: result?.browser?.version || "Unknown",

        event_ts: Date.now().toString(),

        ...eventData,
      };
    } catch (error) {
      console.log("Analytics payload error:", error);

      return null;
    }
  };

  useEffect(() => {
    const fetchUserContestScore = async () => {
      try {
        setScoreLoading(true);

        const response = await userLeaderboard();

        if (!response?.success || !Array.isArray(response?.data)) {
          setUserContestScore(0);
          setUserRank(null);
          return;
        }

        // Sort highest contest score first
        const sortedUsers = [...response.data].sort(
          (a, b) => Number(b.contest_score || 0) - Number(a.contest_score || 0),
        );

        // Find current user
        const currentUserIndex = sortedUsers.findIndex(
          (item) => String(item.user_id).trim() === String(fanduUserId).trim(),
        );

        if (currentUserIndex !== -1) {
          const currentUser = sortedUsers[currentUserIndex];

          setUserContestScore(Number(currentUser.contest_score) || 0);

          // Rank starts from 1
          setUserRank(currentUserIndex + 1);
        } else {
          setUserContestScore(0);
          setUserRank(null);
        }
      } catch (error) {
        console.error("Unable to fetch user contest details:", error);

        setUserContestScore(0);
        setUserRank(null);
      } finally {
        setScoreLoading(false);
      }
    };

    if (fanduUserId) {
      fetchUserContestScore();
    } else {
      setUserContestScore(0);
      setUserRank(null);
      setScoreLoading(false);
    }
  }, [fanduUserId]);

  const trackEvent = async ({
    Event_Name,
    Event_Sub_Name = "Janmashtami_Challenge",
    Event_Type = "Screen_Impressions",
    Event_Sub_Type = "",
    Event_MetaData = {},
  }) => {
    try {
      const requestData = offerRequestData({
        Event_Name,
        Event_Sub_Name,
        Event_Type,
        Event_Sub_Type,
        Event_MetaData,
      });

      if (!requestData) return;

      await offerEvent(requestData);
    } catch (error) {
      console.log("Analytics event error:", error);
    }
  };

  useEffect(() => {
    if (pageTrackedRef.current) return;

    pageTrackedRef.current = true;

    trackEvent({
      Event_Name: "Fandu_Janmashtami_Contest",
      Event_Sub_Name: "Janmashtami_Challenge",
      Event_Type: "Screen_Impressions",
      Event_Sub_Type: "Janmashtami_Challenge_Page",

      Event_MetaData: {
        challenge_title: challenge.title,
        total_score: userContestScore,
        start_date: challenge.startDate,
        end_date: challenge.endDate,
      },
    });
  }, []);

  const handleLeaderboard = () => {
    trackEvent({
      Event_Name: "Leaderboard_Click",
      Event_Sub_Name: "Janmashtami_Challenge",
      Event_Type: "Click",
      Event_Sub_Type: "Janmashtami_Impressions",

      Event_MetaData: {
        challenge_title: challenge.title,
        current_score: userContestScore,
      },
    });

    navigate("/leadership");
  };

  const handleDayClick = async (day) => {
    if (isContestPlayed(day.id)) {
      return;
    }

    // Contest 5 remains locked

    // Contest 2, 3, 4 require logged-in user
    if ([2, 3, 4].includes(day.id) && !fanduUserId) {
      return;
    }

    // Quiz
    if (day.type === "quiz") {
      try {
        const response = await getQuizCategoriesQuestions(
          day.category,
          fanduUserId,
        );

        if (!response?.success) {
          ErrorHandling(
            response?.message || "Unable to load quiz. Please try again.",
          );
          return;
        }

        // If API says user already played the quiz
        if (response?.data?.already_played) {
          ErrorHandling("You have already played this contest.");
          return;
        }

        navigate(`/janmashtmi-quiz/${day.category}`);
      } catch (error) {
        ErrorHandling(error?.message || "Unable to check contest status.");
      }

      return;
    }

    // Games
    if (day.gameKey) {
      setSelectedGame(day.gameKey);
    }
  };

  const isContestLocked = (day) => {
    // Contest 1 is always unlocked
    if (day.id === 1) {
      return false;
    }

    // Contest 2, 3 and 4 are unlocked if user exists
    if ([2, 3, 4, 5].includes(day.id)) {
      return !fanduUserId;
    }

    return false;
  };

  const [playedContests, setPlayedContests] = useState(() =>
    getPlayedContests(),
  );

  useEffect(() => {
    const refreshPlayedContests = () => {
      setPlayedContests(getPlayedContests());
    };

    // Refresh when coming back to this page
    refreshPlayedContests();

    return () => {};
  }, []);

  const markContestAsPlayed = (contestId) => {
    const updated = savePlayedContest(contestId);

    setPlayedContests(updated);
  };

  const isContestPlayed = (contestId) => {
    return Boolean(playedContests[String(contestId)]);
  };

  const hasPlayedAnyContest = Object.values(playedContests).some(
    (played) => played === true,
  );

  return (
    <>
      <Header fanduPoints={fanduPoints} />

      <main className={styles.page}>
        <SoftHeader />

        {/* ================= BANNER ================= */}

        <section className={styles.bannerWrapper}>
          <img
            src={rakhiBanner}
            alt={`${challenge.title} Challenge`}
            className={styles.banner}
          />
        </section>

        {/* ================= SCORE ================= */}
        <div style={{ padding: "12px" }}>
          <section className={styles.scoreCard}>
            {/* TOTAL SCORE */}
            <div className={styles.scoreInfo}>
              <p className={styles.scoreLabel}> Score</p>

              <div className={styles.scoreValue}>
                <img className={styles.scoreIcon} src={coin} alt="" />

                <span>{scoreLoading ? "..." : userContestScore}</span>
              </div>
            </div>

            <div className={styles.scoreDivider} />

            {/* USER RANK */}
            <div className={styles.scoreInfo}>
              <p className={styles.scoreLabel}> Rank</p>

              <div className={styles.rankValue}>
                <Crown size={16} style={{ color: "yellow" }} />

                <span>
                  {scoreLoading ? "..." : userRank ? `${userRank}` : "--"}
                </span>
              </div>
            </div>

            <div className={styles.scoreDivider} />

            <button
              type="button"
              className={styles.leaderboardButton}
              onClick={handleLeaderboard}
            >
              <span>View Leaderboard</span>
            </button>
          </section>

          {/* ================= SECTION TITLE ================= */}

          <div className={styles.sectionHeading}>
            <span className={styles.headingDecoration}>✦</span>

            <h1>Janmashtami Contests</h1>

            <span className={styles.headingDecoration}>✦</span>
          </div>

          {!!fanduUserId && (
            <div className={styles.unlockMessage}>
              🔒 Play{" "}
              <span
                style={{
                  fontWeight: "800",
                  marginLeft: "3px",
                  marginRight: "3px",
                }}
              >
                {" "}
                Contest-1{" "}
              </span>
              first to unlock the Fun Games!
            </div>
          )}

          {/* ================= CHALLENGE LIST ================= */}

          <section className={styles.challengeList}>
            {challenge.days.map((day) => (
              <ChallengeCard
                key={day.id}
                day={day}
                onClick={handleDayClick}
                isPlayed={isContestPlayed(day.id)}
                isLocked={isContestLocked(day)}
                shouldBlink={
                  hasPlayedAnyContest &&
                  !isContestPlayed(day.id) &&
                  !isContestLocked(day)
                }
              />
            ))}
          </section>
        </div>
      </main>

      {selectedGame === "math" && (
        <MathGame
          autoStart
          onComplete={() => {
            markContestAsPlayed(2);
          }}
          onClose={() => {
            markContestAsPlayed(2);
            setSelectedGame(null);
          }}
          page="Janmashtmi_Contest"
          isContest={true}
          remark={true}
          time={60}
        />
      )}

      {selectedGame === "numberRain" && (
        <NumberRain
          autoStart
          onComplete={() => {
            markContestAsPlayed(3);
          }}
          onClose={() => {
            markContestAsPlayed(3);
            setSelectedGame(null);
          }}
          page="Janmashtmi_Contest"
          isContest={true}
          remark={true}
          time={60}
        />
      )}

      {selectedGame === "color" && (
        <ColorMatchGame
          autoStart
          onComplete={() => {
            markContestAsPlayed(4);
          }}
          onClose={() => {
            markContestAsPlayed(4);
            setSelectedGame(null);
          }}
          page="Janmashtmi_Contest"
          isContest={true}
          remark={true}
          time={60}
        />
      )}
    </>
  );
}

/* =========================================================
   CHALLENGE CARD
========================================================= */

function ChallengeCard({
  day,
  onClick,
  isPlayed = false,
  isLocked = false,
  shouldBlink = false,
}) {
  const handleCardClick = () => {
    if (isPlayed || isLocked) return;

    onClick(day);
  };

  const yesPlayed = localStorage.getItem("contestPlayed");

  return (
    <article
      onClick={handleCardClick}
      className={`${styles.challengeCard} ${
        isPlayed ? styles.completedCard : ""
      } ${isLocked ? styles.lockedCard : ""} ${
        shouldBlink ? styles.blinkContest : ""
      }`}
    >
      <div
        className={`${styles.dayBadge} ${
          isPlayed || (yesPlayed && day.id == 1)
            ? styles.greenBadge
            : styles.redBadge
        }`}
      >
        {day.day}
      </div>

      <div
        className={`${styles.iconWrapper} ${
          isPlayed || (yesPlayed && day.id == 1)
            ? styles.greenIconWrapper
            : styles.redIconWrapper
        }`}
      >
        {isPlayed ? (
          <Check size={22} strokeWidth={3} />
        ) : (
          <Gamepad2 size={22} strokeWidth={2.5} />
        )}
      </div>

      <div className={styles.cardContent}>
        <p
          className={`${styles.dayTitle} ${
            isPlayed || (yesPlayed && day.id == 1)
              ? styles.greenText
              : styles.redText
          }`}
        >
          Contest-{day.day}
        </p>

        <h2 className={styles.challengeTitle}>{day.title}</h2>

        <div className={styles.duration}>
          <Clock3 size={10} />
          <span>{day.duration}</span>
        </div>
      </div>

      <div className={styles.cardAction}>
        {isPlayed || (yesPlayed && day.id == 1) ? (
          <button type="button" className={styles.playedButton} disabled>
            <Check size={15} strokeWidth={3} />
            <span>Played</span>
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.playButton} ${
              isLocked ? styles.lockedButton : ""
            }`}
            disabled={isLocked}
            onClick={(e) => {
              e.stopPropagation();

              if (!isLocked) {
                onClick(day);
              }
            }}
          >
            {isLocked ? "🔒 Locked" : "Play Now"}
          </button>
        )}
      </div>
    </article>
  );
}

export default RakhiChallenge;
